import { Icon } from '@aibees/shared/components';
import { Entitlement } from '@aibees/shared/definition';
import { classnames } from '@aibees/shared/utils';
import { Grid, Stack, Typography } from '@mui/material';
import { uniqBy } from 'lodash-es';
import { memo } from 'react';
import { useEntitlementActive } from '../../../../hooks/use-entitlement-active';
import { useRenderValueEntitlement } from '../../../../hooks/use-render-value-entitlement';
import { EntitlementRow } from '../entitlement-row';
import * as Styled from './styles';
import * as SX from './sx';
import type { ExpandedGroupContentProps as Props } from './types';

export const ExpandedGroupContent = memo(
  ({ className, details, rowIndex, onToggleIcon, expandedIcons }: Props) => {
    const { isActiveInFree, isActiveInPaid } = useEntitlementActive();
    const { renderFreeValue, renderPaidValue } = useRenderValueEntitlement();

    const renderEntitlement = () => {
      const uniqedItem = uniqBy(
        details.find((items) => items.group === 'no-group')?.entitlements,
        'name'
      );

      return uniqedItem.map((item, itemIndex) => (
        <EntitlementRow key={item.key} item={item} itemIndex={itemIndex} />
      ));
    };

    return (
      <Styled.Wrapper
        className={classnames('ExpandedGroupContent', className)}
        sx={SX.root}
      >
        {details
          .filter((i) => i.group !== 'no-group')
          .map((group, groupIndex) => {
            const isAutoExpanded = false;
            return (
              <Grid
                item
                xs={12}
                key={groupIndex}
                sx={{
                  ...SX.expandedContent,
                  borderColor:
                    expandedIcons[rowIndex]?.[groupIndex] || isAutoExpanded
                      ? '#E4EBFF'
                      : 'transparent',
                }}
              >
                <Stack
                  sx={SX.stack}
                  onClick={() =>
                    !isAutoExpanded && onToggleIcon(rowIndex, groupIndex)
                  }
                >
                  {group.group && (
                    <Typography variant="body2" fontWeight={600}>
                      {group.group}
                    </Typography>
                  )}
                  {!isAutoExpanded && (
                    <Icon
                      name={
                        expandedIcons[rowIndex]?.[groupIndex] ? 'minus' : 'plus'
                      }
                      size={20}
                      sxx={{ cursor: 'pointer' }}
                    />
                  )}
                </Stack>
                {(expandedIcons[rowIndex]?.[groupIndex] || isAutoExpanded) && (
                  <Stack direction="column" gap={1} mt={2}>
                    {group.entitlements.map(
                      (item: Entitlement, itemIndex: any) => (
                        <Grid
                          container
                          key={itemIndex}
                          sx={SX.detailRow(itemIndex)}
                        >
                          <Grid item xs={6}>
                            <Typography variant="body2" fontWeight={400}>
                              {item.name}
                            </Typography>
                          </Grid>
                          <Grid item xs={3.23} sx={SX.grid}>
                            {item.metadata.value ? (
                              <Typography variant="body2" fontWeight={400}>
                                {item.metadata.value}
                              </Typography>
                            ) : (
                              <Icon
                                name="check-circle-outline"
                                size={20}
                                color={
                                  isActiveInFree(item.products)
                                    ? 'success.700'
                                    : 'grey.400'
                                }
                              />
                            )}
                          </Grid>
                          <Grid item xs={2} sx={SX.grid}>
                            {item.metadata.value ? (
                              <Typography variant="body2" fontWeight={400}>
                                {item.metadata.value}
                              </Typography>
                            ) : (
                              <Icon
                                name="check-circle-outline"
                                size={20}
                                color={
                                  isActiveInPaid(item.products)
                                    ? 'success.700'
                                    : 'grey.400'
                                }
                              />
                            )}
                          </Grid>
                        </Grid>
                      )
                    )}
                  </Stack>
                )}
              </Grid>
            );
          })}
        {renderEntitlement()}
      </Styled.Wrapper>
    );
  }
);

ExpandedGroupContent.displayName = 'ExpandedGroupContent';
